import { IFileModel } from "src/Core/ViewModels/File/FileModel";
import { ISupportContact } from "src/reducers/adminSettingsReducer";
import { ComposeMesssageConstants, EmailTemplateConstants, FileConstants, NewDRConstants } from "../../../Core/Common/Constants";
import { AppNotifier } from "../../Common/Notification/AppNotifier";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { Option } from "react-dropdown";
import { DefaultTaxYearConstants } from "src/helper/Constants";

export function validateFileSize(file: any, maxSize?: string, message?: string): boolean {
    const maximumSize = maxSize || FileConstants.MaximumFileSize;
    if (file.size === 0) {
        return false;
    }
    if (file.size > maximumSize) {
        AppNotifier.Error(message || fileSizeExceedsLimit(file.name));
        return false;
    }
    return true;
}

export const isMaxUploadSizeLimitExceeded = (totalSize: number, maxSize?: number): boolean => {
    const maximumSize = maxSize || FileConstants.MaximumTotalFileSize;
    if (totalSize === 0) {
        return false;
    }
    if (totalSize > maximumSize) {
        AppNotifier.Error(ToasterMessages.ERROR.FILE_SIZE_EXCEEDED);
        return false;
    }
    return true;
};

export const fileTypeValidation = (name: string, supportedTypes: string[], errorMessage: string) => {
    const type = getFileExtension(name).toLowerCase();
    const normalizedSupportedTypes = supportedTypes.map(t => t.toLowerCase());
    if (normalizedSupportedTypes.includes(type)) {
        return true;
    } else {
        AppNotifier.Error(errorMessage);
        return false;
    }
};

export const fileSizeExceedsLimit = (fileName: string) =>
    `${fileName} cannot be uploaded since the size is greater than 3 GB.`;

export const simplePhoneValidation = (char: string, callback: (char: string) => void) => {
    const regNumber = /^[0-9]+$/;
    if (char.length > 10) return false;
    if (!char || char.match(regNumber)) callback(char);
};
export function isValidEmailAddress(emailAddress: any) {
    const pattern = new RegExp(
        /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?$/i
    );
    return pattern.test(emailAddress);
}

export function getFileExtension(fileName: string) {
    return fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2);
}

export function bytesToSize(bytes: any) {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "n/a";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    if (i === 0) return `${bytes} ${sizes[i]}`;
    return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
}

const addDaysToDate = days => {
    const date = new Date();
    date.setDate(date.getDate() + parseInt(days));
    return convertDate(date);
};

export const convertDate = inputFormat => {
    function pad(s) {
        return s < 10 ? "0" + s : s;
    }
    const d = new Date(inputFormat);
    return [pad(d.getMonth() + 1), pad(d.getDate()), d.getFullYear()].join("/");
};

export const getAddInEmailBodyTemplate = (
    companyName: string,
    firstName: string,
    lastName: string,
    email: string,
    link: string,
    expireDays: string,
    dueDateText: string,
    attachments: IFileModel[],
    supportContact: ISupportContact,
    companyLogoUrl: string,
    dropOffLink: string | null,
    emailTemplate: string,
    oneHubLink: string | null,
    mailText: string
) => {
    let attachmentNames = "";
    attachments.forEach((x, index) => {
        attachmentNames += `<li>${x.name}</li>`;
    });

    const unorderedList = `<ul style="max-width: 570px !important">${attachmentNames}</ul>`;
    const filesEnclosedHtml = attachments.length > 0 ? `<div style="font-weight: 500; margin-top: 25px;max-width: 570px !important">
        File(s) Enclosed:
        ${unorderedList}
    </div>` : `<div style="font-weight: 500; margin-top: 25px;"> No files attached </div>`;
    const expirationDate = addDaysToDate(expireDays);
    const mailBody = replacePlaceholders(mailText, companyName, firstName + " " + lastName, getFormattedExpiryDate(expirationDate));
    let verticalLine = "", oneHubButton = "", onehubMessage = "";
    if (oneHubLink && oneHubLink !== "") {
        verticalLine = EmailTemplateConstants.VERTICAL_LINE_HTML;
        oneHubButton = EmailTemplateConstants.ONE_HUB_BUTTON_HTML;
        onehubMessage = EmailTemplateConstants.ONE_HUB_MESSAGE_HTML;
    }
    emailTemplate = emailTemplate
        .replaceAll("#FIRSTNAME#", firstName)
        .replaceAll("#LASTNAME#", lastName)
        .replace("#EMAIL#", email)
        .replace(/(#WEBROOT#)/g, link)
        .replace("#EXPIRES#", getFormattedExpiryDate(expirationDate) + ".")
        .replace("#DUEDATESTRING#", dueDateText)
        .replace("#COMPANYNEWLOGO#", companyLogoUrl)
        .replaceAll("#HELPNAME#", supportContact.firstName + " " + supportContact.lastName)
        .replace("#HELPEMAIL#", supportContact.emailAddress)
        .replace("#HELPPHONE#", supportContact.phoneNumber ?? "")
        .replace("#FILESENCLOSED#", filesEnclosedHtml)
        .replace("#COMPANYNAME#", companyName)
        .replace("%VERTICALLINE%", verticalLine)
        .replace("%ONEHUBBUTTON%", oneHubButton)
        .replace("%ONEHUBMESSAGE%", onehubMessage)
        .replace("#ONEHUBLINK#", oneHubLink)
        .replace("#MAILTEXT#", mailBody);
    if (!dropOffLink) {
        emailTemplate = emailTemplate.replace("#COMPANYDROPOFFURL#", "");
    } else {
        emailTemplate = emailTemplate.replace("#COMPANYDROPOFFURL#", dropOffLink);
    }

    return emailTemplate + "<br /><br />";
};

export const getFormattedExpiryDate = (
    expiresDate: string
) => {
    const dateObject: Date = new Date(expiresDate);
    const formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" });
    return formatter.format(dateObject);
};

export const getSendDREmailBodyTemplate = (
    companyName: string,
    firstName: string,
    lastName: string,
    email: string,
    dueDate: string | null,
    dueDateText: string | null,
    companyLogoUrl: string,
    uploadFilesLink: string,
    emailTemplate: string,
    dropOffLink: string | null,
    oneHubLink: string | null,
    mailText: string
) => {
    const mailBody = replacePlaceholders(mailText, companyName, firstName + " " + lastName);
    let verticalLine = "", oneHubButton = "", onehubMessage = "";
    if (oneHubLink && oneHubLink !== "") {
        verticalLine = EmailTemplateConstants.VERTICAL_LINE_HTML;
        oneHubButton = EmailTemplateConstants.ONE_HUB_BUTTON_HTML;
        onehubMessage = EmailTemplateConstants.ONE_HUB_MESSAGE_HTML;
    }
    emailTemplate = emailTemplate
        .replaceAll("#FIRSTNAME#", firstName)
        .replaceAll("#LASTNAME#", lastName)
        .replaceAll("#COMPANYNAME#", companyName)
        .replace("#DUEDATE#", dueDate)
        .replace("#DUEDATESTRING#", dueDateText)
        .replace("#COMPANYNEWLOGO#", companyLogoUrl)
        .replaceAll("#EMAIL#", email)
        .replace("#WEBROOT#", uploadFilesLink)
        .replace("%VERTICALLINE%", verticalLine)
        .replace("%ONEHUBBUTTON%", oneHubButton)
        .replace("%ONEHUBMESSAGE%", onehubMessage)
        .replace("#ONEHUBLINK#", oneHubLink)
        .replace("#MAILTEXT#", mailBody);
    if (!dueDate) {
        emailTemplate = emailTemplate.replace(NewDRConstants.SEND_DR_EMAIL_DUE_DATE_SENTENCE, "").replace("<br/>", "");
    }
    if (!dropOffLink) {
        emailTemplate = emailTemplate.replace("#COMPANYDROPOFFURL#", "");
    } else {
        emailTemplate = emailTemplate.replace("#COMPANYDROPOFFURL#", dropOffLink);
    }
    return emailTemplate + "<br /><br />";
};

const replacePlaceholders = (mailText: string, companyName: string, userName: string, expiryDate?: string): string => {
    if (mailText) {
        mailText = mailText.replace(/<COMPANYNAME>/g, companyName);
        mailText = mailText.replace(/<SENDERNAME>/g, userName);
        if (expiryDate) {
            mailText = mailText.replace(
                /<EXPIRYDATE>/g,
                `<span style="font-weight: 700 !important">${expiryDate}</span>`
            );
        }
    }
    return `${mailText}`;
};

export const getDefaultTaxYearOptions = (): Option[] => {
    const years: Option[] = [];

    const currentYear = new Date().getFullYear();
    const startYear = DefaultTaxYearConstants.DEFAULT_TAX_YEAR_LOWER_LIMIT;
    const endYear = currentYear + 1;
    for (let year = endYear; year >= startYear; year--) {
        years.push({ value: year.toString(), label: year.toString() });
    }
    return years;
};